import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const blank = { name: "", email: "", phone: "", address: "", notes: "" };

export default function ClientsTab() {
  const [clients, setClients] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/clients").then((r) => setClients(r.data)).catch(() => setClients([]));
  };
  useEffect(load, []);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/clients/${editing.id}`, form);
        toast.success("Client updated");
      } else {
        await api.post("/clients", form);
        toast.success("Client added");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error("Could not save client", { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (c) => {
    try {
      await api.delete(`/clients/${c.id}`);
      setClients((prev) => prev.filter((x) => x.id !== c.id));
      toast.success(`${c.name} deleted`);
    } catch (e) {
      toast.error("Could not delete", { description: formatApiError(e) });
    }
  };

  if (clients === null) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-brand-crimson" data-testid="clients-loading-spinner" />
      </div>
    );
  }

  return (
    <div data-testid="clients-tab">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-white/50">{clients.length} client{clients.length === 1 ? "" : "s"} on file</p>
        <Button onClick={() => { setEditing(null); setForm(blank); setDialogOpen(true); }} data-testid="admin-add-client-button" className="bg-brand-crimson hover:bg-brand-crimhover text-white font-eyebrow text-[11px] uppercase tracking-[0.2em] font-bold">
          <Plus className="h-4 w-4" /> Add Client
        </Button>
      </div>

      {clients.length === 0 ? (
        <div className="rounded-lg border border-brand-line bg-brand-surface p-16 text-center text-white/50" data-testid="clients-empty">
          <Users className="h-10 w-10 mx-auto text-white/30" />
          <p className="mt-4">No clients yet. Add one - their details pull straight into new invoices.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {clients.map((c) => (
            <div key={c.id} className="rounded-lg border border-brand-line bg-brand-surface p-5 hover:border-brand-crimson/50 transition-colors" data-testid={`client-card-${c.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-display font-bold text-white" data-testid={`client-name-${c.id}`}>{c.name}</p>
                  {c.email && <p className="text-xs text-white/50 mt-1">{c.email}</p>}
                  {c.phone && <p className="text-xs text-white/50">{c.phone}</p>}
                </div>
                <div className="flex gap-1.5">
                  <button onClick={() => { setEditing(c); setForm({ ...blank, ...c }); setDialogOpen(true); }} data-testid={`client-edit-${c.id}`} className="p-2 rounded-md text-white/40 hover:text-white hover:bg-white/10 transition-colors" aria-label={`Edit ${c.name}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => remove(c)} data-testid={`client-delete-${c.id}`} className="p-2 rounded-md text-white/40 hover:text-red-400 hover:bg-red-500/10 transition-colors" aria-label={`Delete ${c.name}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              {c.address && <p className="mt-3 text-xs text-white/40 whitespace-pre-line border-t border-brand-line pt-3">{c.address}</p>}
              {c.notes && <p className="mt-2 text-xs text-brand-gold/80">{c.notes}</p>}
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-brand-surface border-brand-line max-w-lg max-h-[85vh] overflow-y-auto" data-testid="client-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-white">{editing ? `Edit ${editing.name}` : "Add Client"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-white/70 text-xs">Name *</Label>
              <Input data-testid="client-form-name" value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="Example Events Ltd" className="bg-brand-obsidian border-brand-line" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-white/70 text-xs">Email</Label>
                <Input data-testid="client-form-email" type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} placeholder="accounts@client.co.uk" className="bg-brand-obsidian border-brand-line" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-white/70 text-xs">Phone</Label>
                <Input data-testid="client-form-phone" value={form.phone} onChange={(e) => setField("phone", e.target.value)} placeholder="07700 900000" className="bg-brand-obsidian border-brand-line" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/70 text-xs">Address</Label>
              <Textarea data-testid="client-form-address" value={form.address} onChange={(e) => setField("address", e.target.value)} rows={3} placeholder={"12 Venue Way\nManchester\nM1 2AB"} className="bg-brand-obsidian border-brand-line" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/70 text-xs">Notes</Label>
              <Textarea data-testid="client-form-notes" value={form.notes} onChange={(e) => setField("notes", e.target.value)} rows={2} placeholder="Anything worth remembering..." className="bg-brand-obsidian border-brand-line" />
            </div>
            <Button onClick={save} disabled={busy} data-testid="client-save-button" className="w-full bg-brand-crimson hover:bg-brand-crimhover text-white font-eyebrow uppercase tracking-[0.2em] text-xs font-bold py-6">
              {busy ? "Saving..." : editing ? "Save Changes" : "Add Client"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
